import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Save, RotateCcw, ExternalLink, Check, CalendarClock } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { useSiteConfig } from "@/context/SiteConfigContext";
import PackageEditor from "@/components/admin/PackageEditor";
import AddOnEditor from "@/components/admin/AddOnEditor";
import ContactEditor from "@/components/admin/ContactEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const clean = (draft) => ({
  ...draft,
  packages: draft.packages.map((p) => ({
    ...p,
    name: p.name.trim(),
    price: p.price.trim(),
    includes: p.includes.map((s) => s.trim()).filter(Boolean),
  })),
  add_ons: draft.add_ons
    .map((a) => ({ ...a, name: a.name.trim(), price: a.price.trim() }))
    .filter((a) => a.name || a.price),
  contact: { phone: draft.contact.phone.trim(), email: draft.contact.email.trim() },
});

export const Admin = () => {
  const { user } = useAuth();
  const { config, loaded, save, reset } = useSiteConfig();
  const [draft, setDraft] = useState(null);
  const [status, setStatus] = useState({ type: "", msg: "" });
  const [busy, setBusy] = useState(false);
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    if (loaded && !draft) setDraft(JSON.parse(JSON.stringify(config)));
  }, [loaded, config, draft]);

  useEffect(() => {
    if (user?.role === "admin") {
      axios
        .get(`${API}/track/scheduler-open`, { withCredentials: true })
        .then(({ data }) => setMetrics(data))
        .catch(() => {});
    }
  }, [user]);

  if (user === null || (user && !draft)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050505] text-zinc-400" data-testid="admin-loading">
        <span className="font-display text-sm uppercase tracking-[0.3em]">Loading…</span>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/account" replace />;

  const dirty = JSON.stringify(draft) !== JSON.stringify(config);

  const onSave = async () => {
    setBusy(true);
    setStatus({ type: "", msg: "" });
    try {
      const body = clean(draft);
      const missing = body.add_ons.find((a) => !a.name || !a.price) || body.packages.find((p) => !p.name || !p.price);
      if (missing) throw new Error("Every package and add-on needs a name and a price.");
      const saved = await save(body);
      setDraft(JSON.parse(JSON.stringify(saved)));
      setStatus({ type: "ok", msg: "Saved — the site is updated." });
    } catch (err) {
      setStatus({ type: "err", msg: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally {
      setBusy(false);
    }
  };

  const onReset = async () => {
    if (!window.confirm("Restore the original prices, add-ons and contact placeholders?")) return;
    setBusy(true);
    try {
      const data = await reset();
      setDraft(JSON.parse(JSON.stringify(data)));
      setStatus({ type: "ok", msg: "Restored defaults." });
    } catch (err) {
      setStatus({ type: "err", msg: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#050505] pb-32 text-white">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 md:px-10">
          <div className="flex items-center gap-6">
            <Logo />
            <span className="hidden font-display text-xs font-semibold uppercase tracking-[0.3em] text-zinc-500 sm:block">
              Owner dashboard
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="admin-view-site-link"
              className="hidden items-center gap-2 px-3 py-2 font-display text-xs font-semibold uppercase tracking-[0.25em] text-zinc-400 transition-colors duration-300 hover:text-white sm:flex"
            >
              <ExternalLink size={14} />
              View site
            </Link>
            <button
              type="button"
              onClick={onSave}
              disabled={busy || !dirty}
              data-testid="admin-save-button"
              className="inline-flex items-center gap-2 bg-[#E60000] px-6 py-2.5 font-display text-xs font-semibold uppercase tracking-[0.25em] text-white transition-[background-color,transform,opacity] duration-300 hover:-translate-y-0.5 hover:bg-[#FF1A1A] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0"
            >
              {status.type === "ok" && !dirty ? <Check size={14} strokeWidth={3} /> : <Save size={14} />}
              {busy ? "Saving…" : dirty ? "Save changes" : "Saved"}
            </button>
          </div>
        </div>
      </header>

      <main className="relative mx-auto max-w-6xl space-y-14 px-5 pt-10 md:px-10 md:pt-14">
        <div>
          <Link
            to="/account"
            data-testid="admin-back-to-account-link"
            className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-zinc-400 transition-colors duration-300 hover:text-[#E60000]"
          >
            <ArrowLeft size={15} />
            Account
          </Link>
          <h1 className="font-display mt-6 text-4xl font-bold uppercase leading-none tracking-tight sm:text-5xl" data-testid="admin-heading">
            Edit your <span className="text-outline-red">menu</span>
          </h1>
          <p className="mt-4 max-w-lg text-sm leading-relaxed text-zinc-400">
            Changes go live on the site the moment you hit save. Welcome back, {user.name}.
          </p>
        </div>

        {metrics && (
          <div
            data-testid="admin-scheduler-metric"
            className="flex items-center gap-5 border border-white/10 bg-white/[0.03] px-6 py-5"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#E60000]/15 text-[#E60000]">
              <CalendarClock size={22} strokeWidth={1.75} />
            </div>
            <div>
              <div className="font-display text-3xl font-bold leading-none text-white" data-testid="admin-scheduler-count">
                {metrics.count}
              </div>
              <p className="mt-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
                Times the scheduler was opened
                {metrics.last_opened_at && (
                  <span className="ml-2 normal-case tracking-normal text-zinc-600">
                    · last {new Date(metrics.last_opened_at).toLocaleDateString()}
                  </span>
                )}
              </p>
            </div>
          </div>
        )}

        {status.msg && (
          <div
            role="status"
            data-testid={`admin-status-${status.type}`}
            className={`border px-4 py-3 text-sm ${
              status.type === "ok" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-[#E60000]/50 bg-[#E60000]/10 text-[#FF6B6B]"
            }`}
          >
            {status.msg}
          </div>
        )}

        <ContactEditor contact={draft.contact} onChange={(contact) => setDraft({ ...draft, contact })} />
        <PackageEditor packages={draft.packages} onChange={(packages) => setDraft({ ...draft, packages })} />
        <AddOnEditor addOns={draft.add_ons} onChange={(add_ons) => setDraft({ ...draft, add_ons })} />

        <div className="flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={onReset}
            disabled={busy}
            data-testid="admin-reset-button"
            className="inline-flex items-center gap-2 font-display text-xs font-semibold uppercase tracking-[0.25em] text-zinc-500 transition-colors duration-300 hover:text-[#E60000]"
          >
            <RotateCcw size={14} />
            Restore defaults
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={busy || !dirty}
            data-testid="admin-save-button-bottom"
            className="inline-flex items-center gap-2 bg-[#E60000] px-8 py-3.5 font-display text-sm font-semibold uppercase tracking-[0.25em] text-white transition-[background-color,transform,opacity] duration-300 hover:-translate-y-0.5 hover:bg-[#FF1A1A] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Save size={15} />
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </main>
    </div>
  );
};

export default Admin;
